/**
 * Modal Component
 * Dialog modal with focus trap, backdrop interaction, and smooth animations
 * Uses @headlessui/react Dialog for accessibility
 */

import React, { useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ModalProps } from '@/types';
import { logger } from '@/utils/logger';

/**
 * Modal dialog component with accessibility features
 * Supports multiple sizes, smooth animations, and backdrop interaction
 *
 * @component
 * @example
 * // Basic modal
 * const [isOpen, setIsOpen] = React.useState(false);
 * <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} title="Confirm">
 *   Are you sure?
 * </Modal>
 *
 * @example
 * // Large modal with custom content
 * <Modal isOpen={isOpen} onClose={onClose} title="Edit Profile" size="lg">
 *   <EditForm onSave={onClose} />
 * </Modal>
 *
 * @param props - ModalProps interface
 * @returns JSX.Element
 */
const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
}) => {
  // Debug logging on mount and state changes
  useEffect(() => {
    logger.debug(`Modal state changed`, {
      isOpen,
      title,
      size,
    });
  }, [isOpen, title, size]);

  // Size styles for modal content
  const sizeClasses: Record<string, string> = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
  };

  /**
   * Handle escape key press - close modal
   */
  const handleEscapeKey = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      logger.debug(`Escape key pressed in modal`);
      onClose();
    }
  };

  /**
   * Handle backdrop click - close modal
   */
  const handleBackdropClick = () => {
    logger.debug(`Modal backdrop clicked`);
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={onClose}
        onKeyDown={handleEscapeKey}
      >
        {/* Backdrop with blur effect */}
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 bg-walrus-black bg-opacity-75 backdrop-blur-sm"
            onClick={handleBackdropClick}
            aria-hidden="true"
          />
        </Transition.Child>

        {/* Dialog positioning container */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={`w-full ${sizeClasses[size]} transform overflow-hidden rounded-lg bg-walrus-charcoal p-6 text-left align-middle shadow-xl transition-all border border-walrus-gray`}
                role="region"
                aria-labelledby="modal-title"
              >
                {/* Modal header */}
                {title && (
                  <div className="mb-4 flex items-center justify-between">
                    <Dialog.Title
                      as="h2"
                      className="text-lg font-bold text-walrus-white"
                      id="modal-title"
                    >
                      {title}
                    </Dialog.Title>
                    {/* Close button */}
                    <button
                      onClick={onClose}
                      className="rounded-lg p-1 text-walrus-silver transition-colors hover:bg-walrus-gray hover:text-walrus-white focus:outline-none focus:ring-2 focus:ring-white"
                      aria-label="Close modal"
                      type="button"
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                )}

                {/* Modal content */}
                <div className="mt-4 text-walrus-silver">{children}</div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

Modal.displayName = 'Modal';

export default Modal;
